import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, LogIn, LineChart, TrendingUp, FileText, FileSignature, Contact,
  MapPin, Database, Scale, Users, Slack, Bell, ShieldCheck, CalendarDays, DoorOpen, RefreshCw, IdCard, Receipt, Activity,
} from "lucide-react";
import Header from "../components/dashboard/Header";
import { GuideShot } from "../components/GuideShot";
import { useAuth } from "../context/AuthContext";

const H = ({ children }) => <h3 className="font-serif text-xl text-brand-ink mt-6 first:mt-0 mb-2">{children}</h3>;
const P = ({ children }) => <p className="text-sm text-brand-muted leading-relaxed mb-3">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-5 space-y-1.5 text-sm text-brand-muted mb-3 marker:text-brand-tertiary">{children}</ul>;
const OL = ({ children }) => <ol className="list-decimal pl-5 space-y-1.5 text-sm text-brand-muted mb-3 marker:text-brand-tertiary">{children}</ol>;
const B = ({ children }) => <strong className="text-brand-ink font-medium">{children}</strong>;
const Tip = ({ children }) => (
  <div className="rounded-lg border border-brand-gold/40 bg-brand-gold/8 px-4 py-3 text-[13px] text-brand-ink mb-3">{children}</div>
);

function buildSections() {
  return [
    {
      id: "getting-started", title: "Getting Started", icon: LogIn, admin: false,
      body: (
        <>
          <P>Welcome to the Bond Collective investor hub — your home for financial performance, sales activity, proposals, and the deal pipeline.</P>
          <H>Signing in & navigating</H>
          <GuideShot name="home" caption="Your Home page" />
          <UL>
            <li>Sign in with your Bond email and password. After signing in you land on <B>Home</B> — your shortcuts, to-dos and upcoming contract renewals.</li>
            <li>The <B>left menu</B> groups every page by area (Members, Sales &amp; Marketing, Money…). Use <B>Find a page</B> or press <B>Ctrl/⌘ K</B> to jump anywhere.</li>
            <li>Set <B>Viewing</B> to the location you're working on — pages open filtered to it.</li>
            <li>The header shows <B>“Data as of {`{month}`}”</B> — the most recent month of uploaded financials.</li>
          </UL>
          <H>Your role</H>
          <UL>
            <li><B>Admin</B> — full access, including backend setup (uploads, users, locations, integrations).</li>
            <li><B>Regional</B> — manages their assigned locations: views financials, budget &amp; marketing for those locations and edits Sales, CRM &amp; Proposals for them.</li>
            <li><B>Community</B> — edits Sales, CRM &amp; Proposals for their assigned locations. No financials.</li>
            <li><B>Investor</B> — read-only: financial dashboards, shared reports, and Sales. No marketing or CRM.</li>
          </UL>
        </>
      ),
    },
    {
      id: "financials", title: "Financials Dashboard", icon: LineChart, admin: false,
      body: (
        <>
          <P>The home screen summarizes portfolio performance from the monthly workbooks an admin uploads.</P>
          <GuideShot name="dashboard" caption="Financials dashboard" />
          <UL>
            <li>Use the <B>location filter</B> to switch between the whole portfolio and a single location.</li>
            <li>KPI cards show Revenue, Net Income, Expenses, and Occupancy with period-over-period change.</li>
            <li>Numbers reflect the latest uploaded month; if something looks a month behind, an admin needs to upload the newer workbook.</li>
          </UL>
        </>
      ),
    },
    {
      id: "sales", title: "Entering & Viewing Sales", icon: TrendingUp, admin: false,
      body: (
        <>
          <P>The <B>Sales</B> page has two tabs: <B>Overview</B> (read-only summary) and <B>Working Tools</B> (where staff enter data). Pick a specific location to enter data.</P>
          <H>Weekly entries — pulled from the CRM, confirmed by you</H>
          <P>Each week's numbers fill in automatically: <B>tours booked</B> come from HubSpot, while <B>tours that showed</B> and <B>deals won</B> come from what you mark in the Hub's CRM. Your job is to check them and confirm.</P>
          <GuideShot name="sales" caption="Sales → Working Tools (pick a location)" />
          <OL>
            <li>On <B>Working Tools</B>, weeks you haven't confirmed yet show as <B>Not confirmed yet</B>. Click <B>Review &amp; confirm</B>.</li>
            <li>The CRM numbers and the names behind them are filled in. Fix anything that's off, add any closed deals the CRM missed, then click <B>Confirm &amp; Save Week</B>.</li>
            <li>Use <B>Add Week</B> for any other week; it pre-fills from the CRM the same way.</li>
          </OL>
          <GuideShot name="sales-editor" caption="Reviewing a week" />
          <H>Move-outs / notices</H>
          <UL>
            <li>Click <B>Add Notice</B> to record a move-out (company, office, notice & vacate dates, monthly rent, and whether the space was resold).</li>
            <li>Upcoming notices stay in the “Upcoming” list; once a vacate date passes they roll into the vacated history.</li>
          </UL>
          <H>How months are shown</H>
          <P>To keep things tidy, only <B>this month and last month</B> show in full detail. Older months collapse into <B>roll-up rows</B> (totals) that you can click to expand. The Overview tab shows a “this month at a glance” recap (new business, move-outs, net new MRR).</P>
        </>
      ),
    },
    {
      id: "reports", title: "Running & Sharing Reports", icon: FileText, admin: true,
      body: (
        <>
          <P>Reports turn the financials into a clean, shareable summary for investors.</P>
          <OL>
            <li>Go to <B>Reports</B> → create a report and choose the period.</li>
            <li>Toggle the sections you want; KPIs auto-fill from the underlying data.</li>
            <li>Publish, then use the <B>Link</B> button to share a read-only URL with investors.</li>
          </OL>
          <Tip>If a saved report predates your latest upload, the editor shows a <B>“Newer data available — Update to {`{month}`}”</B> button. Click it to advance the report's period.</Tip>
        </>
      ),
    },
    {
      id: "proposals", title: "Proposals & Agreement Requests", icon: FileSignature, admin: false,
      body: (
        <>
          <P>Build a workspace proposal to share available offices and pricing with a prospect.</P>
          <OL>
            <li>Go to <B>Proposals</B> → create a proposal, add the location(s) and the specific office(s)/pricing.</li>
            <li>Publish it and share the link with your prospect.</li>
            <li>On the proposal, the prospect taps the term they want on an office and submits a short form to <B>request an agreement</B> (this is not a commitment).</li>
          </OL>
          <H>What happens on a request</H>
          <UL>
            <li>The request is flagged on the <B>CRM board</B> and in the <B>Proposals</B> tab.</li>
            <li>The proposal's creator gets an <B>email</B>, and the <B>Slack channel</B> for each selected location gets a message with an <B>“Open the deal”</B> button.</li>
            <li>The prospect automatically receives a confirmation email.</li>
          </UL>
        </>
      ),
    },
    {
      id: "crm", title: "CRM Pipeline", icon: Contact, admin: false,
      body: (
        <>
          <P>The <B>CRM Pipeline</B> is a kanban board of your deals by stage (e.g. Tour Booked → Proposal Sent → Agreement Sent → Signed).</P>
          <GuideShot name="crm" caption="CRM Pipeline" />
          <UL>
            <li>Drag a lead card between stages as it progresses; every move is timestamped.</li>
            <li>Cards synced from HubSpot show a <B>⧉ HubSpot</B> link. Multiple deals for one prospect group into a single card with a <B>+N deals</B> badge.</li>
            <li>Leads inactive for 14 days get a <B>stale</B> flag so nothing slips.</li>
            <li>Creating a proposal from a lead links them, so you can jump between the deal and its proposal.</li>
          </UL>
        </>
      ),
    },
    {
      id: "bookings", title: "Conference Room Bookings", icon: CalendarDays, admin: false,
      body: (
        <>
          <P>The <B>Bookings</B> page shows live conference-room schedules pulled straight from <B>Yardi Kube</B> — no more checking Google Calendar.</P>
          <GuideShot name="bookings" caption="Room Bookings & day passes" />
          <UL>
            <li>Pick a <B>date</B> (arrows or the date box) and a <B>location</B>. Each meeting room shows a day timeline from 7am–9pm.</li>
            <li>A room with no blocks is <B>free all day</B>; colored blocks are existing bookings, labelled by company.</li>
            <li><B>Click any booking</B> to open its card — the company, who booked it, their email, the time, and the number of attendees.</li>
          </UL>
          <Tip>Only rooms Yardi lists as <B>Meeting Room</B> appear here. Bookings are read-only for now (create/edit still happens in Yardi).</Tip>
        </>
      ),
    },
    {
      id: "availability", title: "Office Availability & Floor Plans", icon: DoorOpen, admin: false,
      body: (
        <>
          <P>The <B>Availability</B> page is your live view of office inventory from Yardi — what's open now and what's about to free up.</P>
          <GuideShot name="availability" caption="Availability" />
          <UL>
            <li>The top cards show totals: <B>Available</B>, <B>On notice</B>, <B>Occupied</B>, and total offices. Pick a location to drill in.</li>
            <li><B>Available now</B> lists open offices with desks and month-to-month / 6-mo / 12-mo pricing.</li>
            <li><B>On notice — freeing soon</B> lists offices a member is vacating, with the current occupant and the <B>date it frees up</B>.</li>
          </UL>
          <H>Interactive floor plan (admin)</H>
          <P>Tie each office to its spot on the floor plan so the whole team can see availability at a glance.</P>
          <OL>
            <li>Open <B>Availability</B> → pick the location. Its floor plan appears (PDF plans are rendered automatically; multi-floor PDFs show one image per floor).</li>
            <li>Click <B>Edit map</B>, then <B>drag a box</B> over an office on the plan and pick its number from the dropdown. Repeat for each office.</li>
            <li>Click <B>Save map</B>. Boxes now color by live status — <B>green</B> available, <B>amber</B> on notice, <B>grey</B> occupied — and clicking a box shows that office's card.</li>
          </OL>
          <Tip>You only map an office's position once. Status colors update automatically every night from the Yardi sync.</Tip>
        </>
      ),
    },
    {
      id: "members", title: "Member Lookup", icon: IdCard, admin: false,
      body: (
        <>
          <P><B>Member Lookup</B> lists every current and former member from Yardi — offices, contract end dates, contacts and billing. Use it for mail sorting too: search a person's name to find their company and office.</P>
          <GuideShot name="members" caption="Member Lookup" />
        </>
      ),
    },
    // ---------------- ADMIN ----------------
    {
      id: "ap", title: "Accounts Payable", icon: Receipt, admin: true,
      body: (
        <>
          <P>Invoices emailed to the billing inbox arrive in <B>Accounts Payable</B> automatically (checked every 15 minutes). The Hub reads each one, suggests the location, and drops receipts, signatures and duplicates.</P>
          <GuideShot name="ap" caption="Accounts Payable" />
          <UL>
            <li>Click an invoice to check the details, set its location and move it through <B>Approved → Scheduled → Paid</B>.</li>
            <li>Getting bills you don't handle? Click <B>Forget vendor</B> — every invoice from them is removed and future ones are blocked. Undo it under <B>Forgotten vendors</B>.</li>
          </UL>
        </>
      ),
    },
    {
      id: "integrations", title: "Integration Health", icon: Activity, admin: true,
      body: (
        <>
          <P><B>Settings → Integrations</B> opens with a live check of every connected service. Green is working, amber needs attention, red is broken — each with the reason in plain English.</P>
          <GuideShot name="integrations" caption="Settings → Integrations" />
        </>
      ),
    },
    {
      id: "new-location", title: "Setting Up a New Location", icon: MapPin, admin: true,
      body: (
        <>
          <P>When Bond opens (or you start marketing) a new location, set it up here:</P>
          <OL>
            <li><B>Proposals → Locations</B>: add the location with its address, description, photos, amenities, and floor plans.</li>
            <li>Add its bookable <B>offices</B> and pricing in the <B>Catalog</B> — or better, click <B>Sync from Yardi</B> to pull Private Offices with live pricing and availability automatically (Coworking is added at $300/mo).</li>
            <li>Set up its <B>Slack channel</B> for agreement alerts (steps below).</li>
            <li>Include the location in your monthly financial workbook so it appears on the dashboard.</li>
          </OL>
          <H>Add the location's Slack alert channel</H>
          <OL>
            <li>Go to <B>api.slack.com/apps</B> → your app (or <B>Create New App → From scratch</B>, name it e.g. “Bond Proposals”, pick your workspace).</li>
            <li>Open <B>Incoming Webhooks</B> → toggle <B>On</B> → <B>Add New Webhook to Workspace</B>.</li>
            <li>Choose the channel for this location → <B>Allow</B> → <B>Copy</B> the webhook URL (<span className="font-mono text-xs">https://hooks.slack.com/services/…</span>).</li>
            <li>In the hub: <B>Proposals → Slack Alerts</B>, paste the URL next to the location and <B>Save channels</B>.</li>
          </OL>
          <Tip>One webhook = one channel. For a <B>private</B> channel, first add the app to it in Slack (channel name → Integrations → Add apps), then it appears in the webhook dropdown. Leave a location blank to use the <B>Default channel</B>.</Tip>
        </>
      ),
    },
    {
      id: "yardi-sync", title: "Yardi Sync & Availability Setup", icon: RefreshCw, admin: true,
      body: (
        <>
          <P>The Office Catalog and Availability pages are powered by a live connection to <B>Yardi Kube</B>. Set it up once per environment (the preview and the live site have separate data).</P>
          <OL>
            <li><B>Link each location to its Yardi property</B> — go to <B>Bookings → Link properties</B> and choose the matching Yardi property for every Bond location. This one-time mapping drives both bookings and the office sync.</li>
            <li><B>Run the first sync</B> — go to <B>Proposals → Office Catalog → Sync from Yardi</B>. It pulls every <B>Private Office</B> with its retail (month-to-month) price, auto-calculates the 6-month (−5%) and 12-month (−10%) rates, and marks each office <B>available</B>, <B>occupied</B>, or <B>on notice</B> (with its free-up date).</li>
            <li><B>Map floor plans</B> — see “Office Availability &amp; Floor Plans” above.</li>
          </OL>
          <Tip>After the first manual sync, the catalog <B>refreshes automatically every night at 3:00 AM ET</B>, so pricing and availability stay current on their own. You can re-run <B>Sync from Yardi</B> any time for an immediate refresh.</Tip>
        </>
      ),
    },
    {
      id: "upload", title: "Monthly Data Upload", icon: Database, admin: true,
      body: (
        <>
          <P>Financials come from the monthly workbook you upload under <B>Monthly Data Upload</B>.</P>
          <UL>
            <li>Only month columns tagged <B>ACTUAL</B> in the workbook are ingested; columns tagged PROJECTION/BUDGET are ignored.</li>
            <li>After a successful upload, the header's <B>“Data as of”</B> advances to the newest ACTUAL month, and dashboards/reports pick it up.</li>
            <li>Upload monthly — each new file adds that month; existing months are replaced with the latest figures.</li>
          </UL>
          <Tip>If the dashboard seems stuck a month behind after uploading, confirm the newest month's column is tagged <B>ACTUAL</B> in the workbook.</Tip>
        </>
      ),
    },
    {
      id: "forecast", title: "Budget vs Actual & Editable Forecast", icon: Scale, admin: true,
      body: (
        <>
          <P><B>Budget vs Actual</B> compares each month's actuals to the plan and projects the full year.</P>
          <UL>
            <li>For months that don't have actuals yet, click <B>Adjust forecast</B> to revise the projected P&amp;L line items — your forecast feeds the full-year outlook while the original plan stays as the budget.</li>
            <li>Use <B>Reset to plan</B> to clear an override.</li>
          </UL>
        </>
      ),
    },
    {
      id: "users", title: "Managing Users & Access", icon: Users, admin: true,
      body: (
        <>
          <P>Manage who can sign in from the <B>Users</B> page.</P>
          <UL>
            <li>Create a user with their Bond email and assign a role: <B>admin</B>, <B>regional</B>, or <B>investor</B>.</li>
            <li>Roles set the default sections a person sees (see “Your role” above).</li>
            <li>The proposal owner's email (used for agreement-request notifications) is their login email — so keep it accurate.</li>
          </UL>
          <Tip>Per-page access toggles per user are on the roadmap — for now, access follows the user's role.</Tip>
        </>
      ),
    },
    {
      id: "notifications", title: "Notifications (Email & Slack)", icon: Bell, admin: true,
      body: (
        <>
          <P>When a prospect requests an agreement, the hub notifies your team automatically:</P>
          <UL>
            <li><B>Email</B> → the proposal's creator, plus a confirmation to the prospect. Sent as “Bond Collective”.</li>
            <li><B>Slack</B> → the channel mapped to each selected office's location (or the default channel), with an <B>“Open the deal”</B> button.</li>
            <li>Configure channels in <B>Proposals → Slack Alerts</B> (see “Setting Up a New Location”).</li>
          </UL>
        </>
      ),
    },
    {
      id: "hubspot", title: "HubSpot Sync", icon: ShieldCheck, admin: true,
      body: (
        <>
          <P>The CRM board can sync bidirectionally with HubSpot.</P>
          <UL>
            <li>Synced deals appear as cards with a <B>⧉ HubSpot</B> link; the sync looks back ~60 days so recently-active older deals still come through.</li>
            <li>Multiple HubSpot deals for the same prospect are de-duplicated into one card.</li>
            <li>Company names come from the contact's company, not the deal name.</li>
          </UL>
        </>
      ),
    },
  ];
}

export default function Guide() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const sections = useMemo(() => buildSections().filter((s) => !s.admin || isAdmin), [isAdmin]);
  const [active, setActive] = useState(sections[0]?.id);

  const go = (id) => {
    setActive(id);
    document.getElementById(`guide-section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-brand-page">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10" data-testid="guide-page">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="h-6 w-6 text-brand-primary" />
          <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Help &amp; Guide</p>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-brand-ink">How to use the hub</h1>
        <p className="text-sm text-brand-muted mt-3 max-w-[70ch]">
          A quick reference for entering data, viewing performance, and running reports.
          {isAdmin && " The Admin sections cover backend setup — adding locations, uploads, users, and integrations."}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10 mt-10">
          <aside className="lg:sticky lg:top-24 self-start" data-testid="guide-toc">
            <nav className="space-y-1">
              {sections.map((s) => (
                <button key={s.id} onClick={() => go(s.id)} data-testid={`guide-toc-${s.id}`}
                  className={`w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-lg text-sm transition-colors ${active === s.id ? "bg-brand-ink text-brand-page" : "text-brand-muted hover:bg-brand-hover hover:text-brand-ink"}`}>
                  <s.icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{s.title}</span>
                  {s.admin && <span className="ml-auto text-[9px] uppercase tracking-wider text-brand-gold">Admin</span>}
                </button>
              ))}
            </nav>
          </aside>

          <div className="space-y-10 min-w-0">
            {sections.map((s, i) => (
              <motion.section key={s.id} id={`guide-section-${s.id}`} data-testid={`guide-section-${s.id}`}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: Math.min(i, 4) * 0.04 }}
                className="scroll-mt-24 border border-brand-border rounded-xl bg-brand-surface p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-9 w-9 rounded-lg bg-brand-hover flex items-center justify-center">
                    <s.icon className="h-5 w-5 text-brand-primary" />
                  </div>
                  <h2 className="font-serif text-2xl text-brand-ink">{s.title}</h2>
                  {s.admin && <span className="ml-auto text-[10px] uppercase tracking-[0.15em] text-brand-gold border border-brand-gold/40 rounded-full px-2.5 py-1">Admin</span>}
                </div>
                {s.body}
              </motion.section>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
